import type { games as en } from '../en/games';

export const games: typeof en = {
  kinds: {
    party: { name: '派对', about: '画画、吹牛、察言观色。' },
    puzzles: {
      name: '解谜',
      about: '找出唯一的答案，慢慢来或比谁快。',
    },
    'spot-and-remember': {
      name: '眼力与记忆',
      about: '好眼力，好记性。',
    },
    strategy: { name: '策略', about: '多想几步。' },
  },
  kindOfGame: '游戏类型',
  all: '全部',
  pickAGame: '选个游戏',
  solo: '单人',
  playSolo: '单人玩',
  playGameSolo: (game) => `单人玩${game}`,
  today: {
    guess: (guess, of) => `第 ${guess} / ${of} 次`,
    done: '今日已完成',
    doneStreak: (days) => `今日已完成 · 连续 ${days} 天`,
    continue: '继续',
    seeResult: '查看今日结果',
  },
  artwork: {
    drawn: 'turtle',
    chainLine: '乌龟',
    chainGuess: '汉堡？',
  },
  counts: {
    'draw-and-guess': (rounds) => `${rounds} 轮`,
    'make-24': (hands) => `${hands} 题`,
    trios: (trios) => `${trios} 组`,
    snap: (cards) => `${cards} 张牌`,
    'liars-dice': (dice) => `每人 ${dice} 颗`,
    hush: (levels) => `${levels} 关`,
    'doodle-chain': (seconds) => `画画 ${seconds} 秒`,
    'daily-word': (words) => `${words} 个单词`,
    'code-breaker': (codes) => `${codes} 题`,
  },
  of: {
    'draw-and-guess': {
      name: '你画我猜',
      tagline: '一人画，其他人抢着猜。词语都是英文。',
      facts: '2–8 人',
      howToPlay: {
        goal: '画一个英文单词让大家猜，也去猜别人画的词。',
        steps: [
          '轮到你时，从三个词里选一个画出来。',
          '其他人在聊天框里猜，随着时间流逝，字母会逐个揭开。',
          '猜中后你的聊天框会锁定到下一回合，免得你说漏嘴。',
        ],
        score: '猜得越快分越高，画画的人也能分到每份得分的一部分。',
      },
      rules: `2–8 人 · 1–4 轮

每人轮流作画。词语都是英文单词，猜的时候也要输入英文。整局只用一个词语类别，所有人都能看到。
画画的人有 15 秒从 3 个词里选一个。超时就自动选第一个。

其他人有 90 秒在聊天框里输入这个词。随着时间流逝，字母会逐个揭开。猜中之后，你的聊天框会锁定到下一回合。

猜得越快分越高，从 50 分到 150 分，画画的人拿到每份得分的五分之二。答案展示 10 秒，然后开始下一回合。`,
      createDescription: '每一轮，每个人都画一次。',
      score: (points) => `${points} 分`,
    },
    minesweeper: {
      name: '扫雷',
      tagline: '自己清一盘，或大家共用一盘、各自暗中选格。',
      facts: '2–8 人',
      howToPlay: {
        goal: '大家共用一盘，各自暗中选安全格。',
        steps: [
          '所有人同时选一个未翻开的格子，没人知道你选了哪格。',
          '数字表示周围八格里有几颗雷。',
          '然后所有选择一起揭晓：安全格翻开，踩到雷就爆。',
        ],
        score: '越冒险的安全格得分越高，踩雷扣分，两人选同一个安全格就平分。',
      },
      rules: `2–8 人 · 共用一盘

所有人同时各选一个未翻开的格子。点击即锁定你的选择，没人看得到你选了哪格。

你有 15 秒选格，然后用 4 秒看大家各选了什么。安全格得 10 分，越冒险加分越多。踩到雷会扣分，格子看起来越安全扣得越多。

和别人选了同一个安全格？奖励平分。超时没选？系统替你选最安全的格子，但没有 10 分的基础分。

棋盘有小、中、大三种，建房时选定。棋盘解完或剩下不到 2 名玩家时，游戏结束。

单人模式是经典扫雷：翻开所有安全格且不踩雷。第一下永远安全。右键或长按格子可以插旗；在旗子都插好的数字上点一下，可以翻开周围其余的格子。每种棋盘的最佳时间会保存在本机。`,
      createDescription: '选一种棋盘。小盘很快，大盘要玩一会儿。',
      solo: {
        summary: '翻开所有安全格。数字表示周围有几颗雷。',
        howToPlay: {
          goal: '翻开所有安全格，不踩到雷。',
          steps: [
            '点一个格子把它翻开；第一下永远安全。',
            '数字表示周围八格里有几颗雷。',
            '右键或长按给雷插旗，再点旗子已插好的数字，翻开周围其余的格子。',
          ],
          score: '越快清完越好：每种棋盘的最佳时间都会保存。',
        },
      },
      score: (points) => `${points} 分`,
    },
    'make-24': {
      name: '24 点',
      tagline: '四个数，加减乘除，算出 24。',
      facts: '2–8 人',
      howToPlay: {
        goal: '用同样的四个数，比别人先算出 24。',
        steps: [
          '每个数用一次，用加减乘除。',
          '选一个数、一个运算符，再选一个数：两张合成一张新牌。',
          '一直算到只剩一张牌。撤销和重来都不扣分。',
        ],
        score: '解出一题得 50 分，越快加分越多。',
      },
      rules: `2–8 人 · 5 题或 10 题

每一题是 1 到 13 之间的四个数，而且一定有解。每个数用一次，用加减乘除算出 24。

选一个数、一个运算符，再选一个数：两张牌合成一张新牌。一直合到只剩一张为止。撤销和重来都不扣分。

所有人同时拿到同一道题，有 60 秒解题。解出来得 50 分，剩余时间最多再加 100 分。别人只知道你解出来了，不知道怎么解的；你的聊天框会等到这一题结束。

单人模式一轮十题，只计一个总时间。卡住了可以跳过这一题，计时加 30 秒。最佳时间保存在本机，“挑战好友”会把同样的十道题发给对方。`,
      createDescription: '每题 60 秒。五题很快就能玩完。',
      solo: {
        summary: '四个数，四种运算，一个目标。每一题都有解。',
        howToPlay: {
          goal: '连续十题，每题用四个数算出 24，只计一个总时间。',
          steps: [
            '每个数用一次，用加减乘除。',
            '选一个数、一个运算符，再选一个数：两张合成一张新牌。',
            '卡住了？跳过这一题，计时加 30 秒。',
          ],
          score: '十题的最佳时间保存在本机。',
        },
      },
      score: (points) => `${points} 分`,
    },
    pairs: {
      name: '翻牌配对',
      tagline: '翻两张牌，记住每张的位置，找出所有的对子。',
      rowTagline: '翻两张牌，记住每张的位置。',
      facts: '2–6 人',
      howToPlay: {
        goal: '比别人找到更多对子。',
        steps: [
          '每个图案都在两张牌上。轮到你时，翻开两张。',
          '翻出一对就归你，并且再来一次。',
          '不是一对：大家都看到这两张，然后翻回去，轮到下一位。',
        ],
        score: '所有对子都找到后，找到最多的人获胜。',
      },
      rules: `2–6 人 · 小或大棋盘

所有牌面朝下，每种图案各有两张。玩家按开局抽定的顺序轮流行动。

轮到你时，有 10 秒翻两张牌。如果一样，这一对归你，并且可以再来一次。如果不一样，所有人看 2 秒，然后牌翻回去，轮到下一位。

超时的话，你单独翻开的那张牌会翻回去。轮到时不在线的玩家会被跳过。所有对子都找到后，找到最多的人获胜。

单人模式用尽量少的回合清空棋盘；回合数相同时比时间。不一样的两张牌一秒后翻回去。每种棋盘的最佳成绩保存在本机，“挑战好友”会把同一副牌发给对方。`,
      createDescription: '轮流翻两张牌。小盘很快，大盘要玩一会儿。',
      solo: {
        summary: '每次翻两张牌，记住每张的位置。',
        howToPlay: {
          goal: '用尽量少的回合清空棋盘。',
          steps: [
            '每个图案都在两张牌上。每次翻两张。',
            '翻出一对就一直朝上。',
            '不是一对：一秒后两张都翻回去，记住它们的位置。',
          ],
          score: '回合越少越好，回合相同时比时间。最佳成绩会保存。',
        },
      },
      score: (pairs) => `${pairs} 对`,
    },
    trios: {
      name: '凑三',
      tagline: '找出三张：每项特征要么全同，要么全异。',
      facts: '2–8 人',
      howToPlay: {
        goal: '在十二张牌里比别人先找出一组。',
        steps: [
          '每张牌有四项特征：颜色、形状、图形个数和填充。',
          '三张牌的每项特征都全部相同或全部不同，就是一组。',
          '没有回合，选三张：第三张一选就提交。选错会被锁 3 秒。',
        ],
        score: '每组得 1 分，找到最多的人获胜。',
      },
      rules: `2–8 人 · 10 组或 20 组

每张牌有四项特征：颜色（珊瑚、蓝、黑）、形状（圆、三角、方）、数量（一、二、三）和填充（实心、条纹、空心）。三张牌要成一组，每项特征单独来看，必须全部相同或全部不同。两个圆加一个方永远不成组。

桌上有十二张牌，其中一定有一组。所有人同时看同一桌牌，没有回合。选三张牌；第三张一选就提交，在那之前没人看得到你的选择。

最先提交的一组归提交者，得 1 分：所有人都会看到这一组和找到它的人，持续 2 秒，然后三张新牌补上。选了不成组的三张会被锁 3 秒，但不扣分。如果 30 秒没人找到，会给所有人标出一组中的一张牌，再过 30 秒标出第二张。

找满 10 组或 20 组后游戏结束。找到最多的人获胜。

单人模式一局十组，只计一个总时间。选错加 5 秒；“提示”会标出一组中的一张牌，持续 10 秒，最多用两次。最佳时间保存在本机，“挑战好友”会把同一副牌发给对方。`,
      createDescription:
        '所有人看同样的十二张牌，谁先选出一组就归谁。10 组大约三分钟。',
      solo: {
        summary: '桌上十二张牌，其中三张成一组。尽快找出十组。',
        howToPlay: {
          goal: '在十二张牌里找出十组，只计一个总时间。',
          steps: [
            '每张牌有四项特征：颜色、形状、图形个数和填充。',
            '三张牌的每项特征都全部相同或全部不同，就是一组。',
            '选错加 5 秒。提示会标出一组里的一张牌，加 10 秒。',
          ],
          score: '最佳时间保存在本机。',
        },
      },
      score: (trios) => `${trios} 组`,
    },
    snap: {
      name: '找相同',
      tagline: '找出两张牌上共有的那个图案。',
      facts: '2–6 人',
      howToPlay: {
        goal: '最先找出你的牌和中间那张牌共有的图案。',
        steps: [
          '任意两张牌恰好有一个相同图案，不多也不少。',
          '没有回合：在任意一张牌上点中那个图案。',
          '最先点中，中间的牌就归你。点错会被锁 2 秒。',
        ],
        score: '拿到一张牌得 1 分，拿到最多的人获胜。',
      },
      rules: `2–6 人 · 31 张牌

每张牌上有 6 个图案，任意两张牌恰好有一个相同图案，不多也不少。每个图案的形状都不一样，不用靠颜色分辨。

每人发一张明牌，再翻开一张放在中间。大家同时看着同一张中间的牌，没有回合。找出你的牌和它共有的那个图案，在任意一张牌上点它。

最先点对的人抢到中间的牌，得 1 分：所有人都会看到是谁抢到的、是哪个图案，片刻之后这张牌就成了他的牌，再翻开下一张。点了只在一张牌上的图案，会被锁 2 秒，但不会扣分。

牌堆翻完，游戏结束。抢到牌最多的人获胜。

单人模式下，一轮是 30 张牌，计总用时。点错一次加 3 秒。你的最佳成绩保存在本机，“挑战好友”会让好友拿到同一副牌。`,
      createDescription:
        '大家比谁先找到自己的牌和中间那张牌共有的图案。一局大约两分钟。',
      solo: {
        summary: '你的牌和中间的牌恰好有一个相同图案。连找 30 次，越快越好。',
        howToPlay: {
          goal: '找出你的牌和中间那张牌共有的图案，连找 30 次，只计一个总时间。',
          steps: [
            '任意两张牌恰好有一个相同图案，不多也不少。',
            '在任意一张牌上点中那个图案，中间的牌就归你。',
            '点错一次加 3 秒。',
          ],
          score: '最佳时间保存在本机。',
        },
      },
      score: (cards) => `${cards} 张牌`,
    },
    'liars-dice': {
      name: '大话骰',
      tagline: '暗中掷骰，押注全桌，揭穿吹牛。',
      facts: '2–6 人',
      howToPlay: {
        goal: '对全桌掷出的骰子叫点，拆穿吹牛，守住你的骰子。',
        steps: [
          '每个人暗中掷骰，然后沿桌轮流叫点。叫“四个 6”就是说全桌至少有四颗 6。1 点是万能的，所以叫点只叫 2 到 6。',
          '轮到你时有 20 秒加注或喊“吹牛”。加注要么颗数更多（点数任意），要么颗数相同、点数更大。',
          '喊“吹牛”后所有骰盅打开。叫的成立，喊的人丢一颗骰子；不成立，叫的人丢一颗。丢骰子的人开启下一轮。',
        ],
        score: '骰子丢光就出局，最后还有骰子的人获胜。',
      },
      rules: `2–6 人 · 每人 3 颗或 5 颗骰子

每个人暗中掷骰，然后轮流叫全桌的骰子：至少有几颗是某个点数。1 点算任意点数，所以没人叫 1。每种点数大约占全桌骰子的三分之一，算上 1 点。

轮到你时，加注或喊“吹牛”。加注是叫更多颗任意点数，或同样颗数但更大的点数。你有 20 秒；超时会替你喊“吹牛”，如果还没人叫过，就替你叫一颗 2。

喊“吹牛”后，所有骰盅打开 5 秒。如果全桌的数量至少和叫的一样多，喊的人丢一颗骰子；否则叫的人丢。丢骰子的人开启下一轮。最后一颗骰子也丢了就出局；最后还有骰子的人获胜。

单人模式和 1 到 5 个机器人对战，没有计时，你准备好了再进入下一轮。胜场保存在本机。`,
      createDescription: '轮流叫全桌的骰子。每人 3 颗很快，5 颗是经典玩法。',
      solo: {
        summary: '暗中掷骰，押注全桌，揭穿吹牛。',
        howToPlay: {
          goal: '对全桌掷出的骰子叫点，拆穿机器人的吹牛，守住你的骰子。',
          steps: [
            '叫“四个 6”就是说全桌至少有四颗 6。1 点是万能的，算任意点数，所以叫点只叫 2 到 6。',
            '轮到你时，加注或喊“吹牛”。加注要么颗数更多（点数任意），要么颗数相同、点数更大。',
            '喊“吹牛”后所有骰盅打开。叫的成立，喊的人丢一颗骰子；不成立，叫的人丢一颗。',
          ],
          score:
            '最后还有骰子的人获胜。任意一个点数，全桌大约有三分之一的骰子算数。',
        },
      },
      score: (dice) => `${dice} 颗骰子`,
    },
    hush: {
      name: '嘘',
      tagline: '不分回合，不许说话。大家一起把牌按顺序出完。',
      facts: '2–4 人',
      howToPlay: {
        goal: '大家一起把所有牌从小到大打到同一叠上。',
        steps: [
          '每个人手里都有 1 到 100 的数字牌，只看得到自己的。',
          '没有回合，不许说话：任何人随时都可以打出自己最小的牌。',
          '有人手里还有更小的牌时你出了牌，全队就丢一条命。',
        ],
        score: '过完所有关卡就一起获胜。一关零失误可以赢回一条命。',
      },
      rules: `2–4 人 · 7、6 或 5 关

每个人手里是 1 到 100 之间的数字牌，全桌要把牌从小到大打到同一叠上。没有回合：任何人随时都可以打出自己最小的牌。谁都不许说话，所以关卡进行中聊天框会锁定。

第 1 关每人发一张牌，第 2 关两张，依此类推。每关开始前所有人按“准备”；然后发牌，3 秒倒计时后开始。

如果有人打出一张牌时，别人手里还有更小的，全队丢一条命：所有更小的牌亮出并弃掉，暂停 3 秒。全队有 3 条命，一关零失误可以赢回一条。

通过最后一关，大家一起获胜。两人玩 7 关，三人 6 关，四人 5 关。有人掉线时关卡暂停，直到对方回来；如果座位被释放，他的牌弃掉，不丢命。`,
      createDescription: '一言不发，大家一起把牌按顺序出完。人越少关越多。',
      score: (levels) => `${levels} 关`,
    },
    'doodle-chain': {
      name: '传话画画',
      tagline: '写一句，画出来，传下去，看它面目全非。',
      facts: '3–8 人',
      howToPlay: {
        goal: '让一句话在桌上以画和文字传下去，看它怎么走样。',
        steps: [
          '每个人先写一句话开始一条传话：画得出来的就行。',
          '之后把拿到的句子画出来，或者用文字说出画的是什么。',
          '你只能看到你前一步的内容。',
        ],
        score: '最后房主逐条揭晓。你的得分是你的内容收到的爱心数。',
      },
      rules: `3–8 人 · 每人一步，最多六步

每个人先写一句话开始一条传话：任何能画出来的东西，用什么语言都行。没灵感？按“换个点子”。然后每条传话都往下传一位，下一个人画出他拿到的这句话，再下一个人用文字说出这幅画画的是什么，下一个人再画这句描述，依此类推。你只能看到你前一步的内容。

每一步所有人同时进行。写 30 秒，描述 45 秒，画画用房间设定的时间。完成后按“完成”，所有人都完成时这一步提前结束；时间到了，你手上的内容原样传下去。

一局每人一步，但最多六步。如果你前一步的内容是空的，这一步你就轮空，传话从再前一条接着走。

最后房主逐条揭晓每条传话，从第一句到最后一句。每条传话里你可以把一颗爱心送给最好笑的那一刻；你的得分就是你的内容收到的爱心。所有传话会留在画册里，直到下一局开始。`,
      createDescription:
        '绕桌写、画、描述，最后一起看每条传话怎么走样。人数就是长度：每人一步，最多六步。',
      score: (hearts) => `${hearts} 颗爱心`,
    },
    'daily-word': {
      name: '每日单词',
      tagline: '五个字母，六次机会。每天一个新单词。',
      facts: '2–8 人',
      howToPlay: {
        goal: '和大家猜同一个五字母单词，用的次数比别人少。',
        steps: [
          '每次猜的必须是真实的五字母单词。',
          '绿色方块：字母对，位置也对。珊瑚色圆圈：在单词里，但在别处。灰色方块：不在单词里。',
          '别人能看到你的标记，看不到你的字母。',
        ],
        score: '用的次数越少得分越高，剩余时间还能再加一点。',
      },
      rules: `2–8 人 · 3 个或 5 个单词

六次机会猜出一个隐藏的五字母英文单词。每次猜的必须是真实单词。每猜一次，每个字母都会被标记：绿色方块表示位置正确，珊瑚色圆圈表示在单词里但位置不对，灰色平格表示不在单词里。

一个字母出现几次就标记几次，位置正确的优先。猜的不是单词，或者已经猜过，会被退回，不计次数。

所有人同时猜同一个单词，每个单词 2 分钟。猜中后，每剩一次机会得 100 分，剩余时间最多再加 50 分。别人能看到你的标记，看不到你的字母；猜中后你的聊天框会等到单词揭晓。

单人模式每天一个单词，所有人相同，午夜换新，按你所在的时区。统计和连胜保存在本机。猜完后可以随意练习，“挑战好友”会把同一个单词发给对方。`,
      createDescription: '每个单词两分钟。三个单词很快就能玩完。',
      solo: {
        summary: '每天一个单词，人人相同。六次机会猜出它。',
        howToPlay: {
          goal: '六次机会猜出隐藏的五字母单词。',
          steps: [
            '每次猜的必须是真实的五字母单词。',
            '绿色方块：字母对，位置也对。珊瑚色圆圈：在单词里，但在别处。灰色方块：不在单词里。',
            '每天一个单词，人人相同，之后可以随意练习。',
          ],
          score: '统计和连胜保存在本机。',
        },
      },
      score: (points) => `${points} 分`,
    },
    'code-breaker': {
      name: '猜数字',
      tagline: '四个数字，十次机会。每天一道新题。',
      facts: '2–8 人',
      howToPlay: {
        goal: '和大家猜同一个四位数，用的次数比别人少。',
        steps: [
          '答案和每次猜的都是四个不同的数字；可以以 0 开头。',
          '每猜一次得到四个标记。绿色方块：数字对，位置也对。珊瑚色圆圈：数字对，位置不对。灰色方块：不在答案里。',
          '标记只数个数，不说是哪几个数字。',
        ],
        score: '用的次数越少得分越高，剩余时间还能再加一点。',
      },
      rules: `2–8 人 · 1 题或 3 题

十次机会猜出一个隐藏的四位数：四个各不相同的数字，0 到 9，可以以 0 开头。每次猜的也必须是四个不同的数字。

每猜一次，旁边的四个标记告诉你有多接近：绿色方块表示有一个数字在答案里且位置正确，珊瑚色圆圈表示有一个数字在答案里但位置不对，灰色方块表示其余的数字。标记只数个数，不说是哪几个数字。已经猜过的会被退回，不计次数。

数字键盘上的笔记只给你自己看：打开“笔记”，点一个数字键就把它记为不在，再点记为在，第三次清除。

所有人同时猜同一个答案，每题 3 分钟。猜中得 100 分，每剩一次机会再加 100 分，剩余时间最多再加 50 分。别人能看到你的标记，看不到你的数字；猜中后要等答案揭晓才能聊天。

单人模式每天一道题，所有人相同，午夜换新，按你所在的时区。统计和连胜保存在本机。猜完后可以随意练习，“挑战好友”会把同一道题发给对方。`,
      createDescription: '每题三分钟。三题大约十分钟。',
      solo: {
        summary: '每天一道题，人人相同。十次机会猜出它。',
        howToPlay: {
          goal: '十次机会猜出隐藏的四位数。',
          steps: [
            '答案和每次猜的都是四个不同的数字；可以以 0 开头。',
            '每猜一次得到四个标记。绿色方块：数字对，位置也对。珊瑚色圆圈：数字对，位置不对。灰色方块：不在答案里。',
            '标记只数个数，不说是哪几个数字。',
          ],
          score: '每天一道题，人人相同。连胜保存在本机。',
        },
      },
      score: (points) => `${points} 分`,
    },
    gomoku: {
      name: '五子棋',
      tagline: '轮流落子，连成五子就赢。',
      facts: '2 人',
      howToPlay: {
        goal: '最先把五颗子连成一线。',
        steps: [
          '第一局的颜色随机决定，黑方先走，之后双方轮流在任意空点上落一颗子。',
          '五颗或更多连成一线就赢：横、竖、斜都算。',
          '每步 30 秒。连续两次超时判负。',
        ],
        score: '赢一局得一分，每局交换颜色。',
      },
      rules: `2 人 · 15 × 15 的棋盘

黑白双方轮流在任意空点上落一颗子，黑方先走。先把五颗或更多同色棋子连成一线的人获胜，横、竖、斜都算。棋盘下满还没有人连成五子，就是平局。

每步有 30 秒；超时就轮到对方，连续两次超时判负。你随时可以认输。第一局的颜色随机决定，之后每局交换，赢一局得一分。最多还能有 5 人观战。

单人模式和电脑对战，分简单、中等、困难三档，可以执黑或执白。胜场保存在本机。`,
      createDescription:
        '两人轮流落子，黑方先走，连成五子获胜。最多还能有五人观战。',
      solo: {
        summary: '轮流落子，横、竖或斜连成五子就赢。',
        howToPlay: {
          goal: '比电脑先把五颗子连成一线。',
          steps: [
            '黑方先走，之后双方轮流在任意空点上落一颗子。',
            '五颗或更多连成一线就赢：横、竖、斜都算。',
            '没有计时，轮到你时还可以悔掉上一步。',
          ],
          score: '棋盘下满算平局。胜场保存在本机。',
        },
      },
      score: (wins) => `${wins} 胜`,
    },
  },
};
